import { toast } from "sonner";
import request from "../../helpers/request";

export interface ManutencaoPayload {
  idLoja: number;
  idEquipamento?: number | null;
  idMonitor?: number | null;
  idsPontosSelecionados?: number[];
  horarioInicio: string;
  horarioFim: string;
  idUsuarioCriador: number;
  observacao: string;

  cliente?: string;
  endereco?: string;
  numeroApolice?: string;
  email?: string;
  telefone?: string;
  descricaoInterrupcao?: string;
  outrosDescricaoInterrupcao?: string;
  tipoManutencao?: string;
  descricaoParadaLocaisAfetados?: string;
  acaoGerenciaLocalJson?: string;
  acaoGerenciaLocalOutros?: string;
  informacoesAdicionais?: string;
  recorrente?: boolean;
  diasRecorrencia?: number[];
}

export interface ManutencaoItem {
  id: number;
  idLoja: number;

  idRegional: number | null;
  nomeLoja: string | null;
  nomeRegional: string | null;

  idEquipamento: number | null;
  idMonitor: number | null;
  horarioInicio: string;
  horarioFim: string;
  idUsuarioCriador: number;
  dataCriacao: string;
  observacao: string;
  cliente: string;
  endereco: string;
  numeroApolice: string;
  email: string;
  telefone: string;
  descricaoInterrupcao: string;
  outrosDescricaoInterrupcao: string;
  tipoManutencao: string;
  descricaoParadaLocaisAfetados: string;
  acaoGerenciaLocalJson: string;
  acaoGerenciaLocalOutros: string;
  informacoesAdicionais: string;
  idsPontosSelecionados: number[];
  status: "PENDENTE" | "APROVADA" | "REJEITADA" | "PRORROGADA" | "CANCELADA";
  idUsuarioAprovador: number | null;
  nomeUsuarioAprovador: string | null;
  dataAprovacao: string | null;
  justificativaRejeicao: string | null;
  instrucoesCliente: string | null;

  recorrente: boolean;
  diasRecorrencia: string | null; // CSV "1,3,5"
  serieAtiva: boolean;
  idManutencaoPai: number | null;
  geradaAutomaticamente: boolean;
  justificativaCancelamento: string | null;
}

export interface ListarManutencoesParams {
  pagina?: number;
  tamanhoPagina?: number;
  status?: string;
  idLoja?: number | null;
  idRegional?: number | null;
  dataInicio?: string;
  dataFim?: string;
}

// idLoja tem prioridade sobre idRegional — mesmo padrão do restante do app
// (lojaSelected > regionalSelected). Envie só um dos dois; se enviar os
// dois, o backend prioriza idLoja e ignora idRegional.

export interface ListarManutencoesResponse {
  dados: ManutencaoItem[];
  totalRegistros: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

export const criarManutencaoProgramada = async (payload: ManutencaoPayload) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/Criar`;
  const result = await request({
    url,
    method: "POST",
    data: payload,
    noProtect: false,
  });

  if (result.status === 200 || result.status === 201) {
    toast.success("Manutenção agendada com sucesso!");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao agendar manutenção.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const suspenderOcorrenciaProgramada = async (
  id: number,
  justificativa: string,
) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/Suspender/${id}`;
  const result = await request({
    url,
    method: "POST",
    data: { justificativa },
    noProtect: false,
  });

  if (result.status === 200) {
    toast.success("Ocorrência suspensa com sucesso!");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao suspender ocorrência.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const pausarSerieManutencao = async (id: number) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/PausarSerie/${id}`;
  const result = await request({ url, method: "POST", noProtect: false });

  if (result.status === 200) {
    toast.success("Série de recorrência pausada.");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao pausar série.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const retomarSerieManutencao = async (id: number) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/RetomarSerie/${id}`;
  const result = await request({ url, method: "POST", noProtect: false });

  if (result.status === 200) {
    toast.success("Série de recorrência retomada.");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao retomar série.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const listarManutencoesProgramadas = async (
  params: ListarManutencoesParams,
): Promise<ListarManutencoesResponse> => {
  const query = new URLSearchParams();
  if (params.pagina) query.set("pagina", String(params.pagina));
  if (params.tamanhoPagina)
    query.set("tamanhoPagina", String(params.tamanhoPagina));
  if (params.status) query.set("status", params.status);
  if (params.idLoja) query.set("idLoja", String(params.idLoja));
  if (params.idRegional) query.set("idRegional", String(params.idRegional));

  const url = `${process.env.REACT_APP_API}/Manutencao?${query.toString()}`;
  const result = await request({ url, method: "GET", noProtect: false });

  if (result.status === 200) return result.data;
  const errorMsg = result.data?.message || "Falha ao listar manutenções.";
  toast.error(errorMsg);
  throw new Error(errorMsg);
};

// Visão de andamento pra usuários convencionais (nível < 6) — escopo por
// loja selecionada, ou pela regional inteira se nenhuma loja específica
// estiver selecionada. Silencioso em vez de toast de erro: essa tela é lida
// com frequência, e se a Loja/Regional ainda não carregou no momento do
// primeiro fetch (ex: aside ainda inicializando), não faz sentido estourar
// um toast de erro pro usuário — só devolve lista vazia via backend.

export const aceitarManutencaoProgramada = async (
  id: number,
  instrucoesCliente: string,
) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/Aceitar/${id}`;
  const result = await request({
    url,
    method: "POST",
    data: { instrucoesCliente },
    noProtect: false,
  });

  if (result.status === 200) {
    toast.success("Manutenção aceita com sucesso!");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao aceitar manutenção.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const rejeitarManutencaoProgramada = async (
  id: number,
  justificativa: string,
) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/Rejeitar/${id}`;
  const result = await request({
    url,
    method: "POST",
    data: { justificativa },
    noProtect: false,
  });

  if (result.status === 200) {
    toast.success("Manutenção rejeitada.");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao rejeitar manutenção.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const prorrogarManutencaoProgramada = async (
  id: number,
  novoHorarioFim: string,
) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/Prorrogar/${id}`;
  const result = await request({
    url,
    method: "POST",
    data: { novoHorarioFim },
    noProtect: false,
  });

  if (result.status === 200) {
    toast.success("Manutenção prorrogada com sucesso!");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao prorrogar manutenção.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};

export const apagarManutencaoProgramada = async (id: number) => {
  const url = `${process.env.REACT_APP_API}/Manutencao/Apagar/${id}`;
  const result = await request({
    url,
    method: "DELETE",
    noProtect: false,
  });

  if (result.status === 200) {
    toast.success("Manutenção apagada.");
    return result.data;
  } else {
    const errorMsg = result.data?.message || "Falha ao apagar manutenção.";
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }
};
